import { apiService } from './apiService';
import { DiaryItem } from '../models/diary';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.eternal-health.com/api';

export interface Ingredient {
  name: string;
  quantity: number;
  calories: number;
  carbohydrates: number;
  proteins: number;
  fats: number;
  water: number | null;
  fiber: number;
}

export interface Meal {
  id: number;
  name: string;
  calories: number;
  ingredients: Ingredient[];
}

export interface ActivityType {
  id: number;
  name: string;
  description: string;
}

export interface Activity {
  id: number;
  name: string;
  activityType: ActivityType | string;
  details: string;
  activityLevel: string;
  expectedDurationMin: number;
}

export interface SupplementCatalog {
  id: number;
  name: string;
  description: string;
  purchaseUrl: string;
  created_at: string;
  updated_at: string;
}

export interface Supplement {
  id: number;
  description: string;
  supplementCatalog: SupplementCatalog;
}

export interface MealTargetAPI {
  id: number;
  calories: number;
  protein: string;
  carbs: string;
  fat: string;
  fiber: string;
  water: string;
}

export interface DiaryItemAPI {
  id: number;
  name: string;
  type: 'MealItem' | 'FitnessItem' | 'SupplementItem' | 'MedicationItem';
  subtype: string;
  meals: Meal[];
  activities?: Activity[];
  supplements?: Supplement[];
  medications?: ExistingMedication[];
  date: string;
  dayOfWeek: string;
  timeOfDay: string;
  totalCalories: number;
  hidden?: boolean;
  mealTarget?: MealTargetAPI;
  isOriginal: boolean;
  effectiveDate: string | null;
  cancellationDate: string | null;
  replacedById: number | null;
  originalItemId: number | null;
}

export interface DiaryItemsResponse {
  count: number;
  perpage: number;
  actualPage: number;
  totalPages: number;
  results: DiaryItemAPI[];
}

// Build the camelCase, numeric mealTarget payload expected by the API.
const buildMealTargetPayload = (target: any) => {
  const num = (v: any) => {
    const n = Number(v);
    return isNaN(n) ? 0 : n;
  };
  return {
    calories: num(target.calories),
    protein: num(target.protein),
    carbs: num(target.carbs),
    fat: num(target.fat),
    fiber: num(target.fiber),
    water: num(target.water),
  };
};

// Helper function to convert API response to DiaryItem format
const convertApiToDiaryItem = (apiItem: DiaryItemAPI): DiaryItem => {
  return {
    id: apiItem.id,
    type: apiItem.type,
    subtype: apiItem.subtype as any, // Type assertion for subtype compatibility
    activities: apiItem.activities || [],
    name: apiItem.name,
    dayOfWeek: apiItem.dayOfWeek,
    meals: apiItem.meals,
    timeOfDay: apiItem.timeOfDay,
    totalCalories: apiItem.totalCalories,
    supplements: apiItem.supplements || [],
    medications: apiItem.medications || [],
    hidden: apiItem.hidden,
    mealTarget: apiItem.mealTarget,
    // API specific fields
    date: apiItem.date,
    isOriginal: apiItem.isOriginal,
    effectiveDate: apiItem.effectiveDate,
    cancellationDate: apiItem.cancellationDate,
    replacedById: apiItem.replacedById,
    originalItemId: apiItem.originalItemId,
  };
};

// API interfaces for existing items
export interface ExistingActivity {
  id: number;
  name: string;
  activityLevel: string;
  activityType: ActivityType;
  details: string;
  expectedDurationMin: number;
}

export interface ExistingSupplement {
  id: number;
  description: string;
  supplementCatalog: {
    id: number;
    name: string;
    description: string;
    purchaseUrl: string;
    created_at: string;
    updated_at: string;
  };
}

export interface ActivitiesResponse {
  message: string;
  data: ExistingActivity[];
  count: number;
}

export interface SupplementsResponse {
  message: string;
  data: ExistingSupplement[];
  count: number;
}

export interface SupplementCatalogResponse {
  count: number;
  perpage: number;
  actual_page: number;
  total_pages: number;
  results: SupplementCatalog[];
}

export interface MedicationCatalog {
  id: number;
  name: string;
  description: string;
  purchaseUrl: string;
  created_at: string;
  updated_at: string;
}

export interface ExistingMedication {
  id: number;
  description: string;
  medicationCatalog: MedicationCatalog;
}

export interface MedicationCatalogResponse {
  count: number;
  perpage: number;
  actual_page: number;
  total_pages: number;
  results: MedicationCatalog[];
}

export const diaryService = {
  // Fetch existing activities from database for a specific user
  getExistingActivities: async (userId: string | number): Promise<ExistingActivity[]> => {
    const url = `${API_BASE_URL}/clinic/activities/?user_id=${userId}`;
    try {
      const response: ActivitiesResponse = await apiService.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching existing activities:', error);
      throw error;
    }
  },

  // Fetch existing supplements from database for a specific user
  getExistingSupplements: async (userId: string | number): Promise<ExistingSupplement[]> => {
    const url = `${API_BASE_URL}/clinic/patient/supplements/${userId}/`;
    try {
      const response: ExistingSupplement[] = await apiService.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching existing supplements:', error);
      throw error;
    }
  },

  // Fetch supplement catalog
  getSupplementCatalog: async (page = 1, perpage = 500): Promise<SupplementCatalog[]> => {
    const url = `${API_BASE_URL}/clinic/supplement-catalog/?page=${page}&perpage=${perpage}`;
    try {
      const response: SupplementCatalogResponse = await apiService.get(url);
      return response.results;
    } catch (error) {
      console.error('Error fetching supplement catalog:', error);
      throw error;
    }
  },

  // Fetch medication catalog
  getMedicationCatalog: async (page = 1, perpage = 500): Promise<MedicationCatalog[]> => {
    const url = `${API_BASE_URL}/clinic/medication-catalog/?page=${page}&perpage=${perpage}`;
    try {
      const response: MedicationCatalogResponse = await apiService.get(url);
      return response.results;
    } catch (error) {
      console.error('Error fetching medication catalog:', error);
      throw error;
    }
  },

  // Fetch activity types
  getActivityTypes: async (): Promise<ActivityType[]> => {
    const url = `${API_BASE_URL}/activity-types/`;
    try {
      const response: ActivityType[] = await apiService.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching activity types:', error);
      throw error;
    }
  },
  createDiaryItem: async (patientId: string | number, newItem: Partial<DiaryItem>): Promise<DiaryItem> => {
    const url = `${API_BASE_URL}/clinic/patient/${patientId}/diary-items/create/`;
    
    // Prepare the API data according to the specification
    const apiData: any = {
      name: newItem.name,
      type: newItem.type,
      dayOfWeek: newItem.dayOfWeek,
      timeOfDay: newItem.timeOfDay,
      subtype: newItem.subtype,
      hidden: newItem.hidden || false
    };

    // Add effectiveDate if provided
    if (newItem.effectiveDate) {
      apiData.effectiveDate = newItem.effectiveDate;
    }

    // Add cancellationDate if provided
    if (newItem.cancellationDate) {
      apiData.cancellationDate = newItem.cancellationDate;
    }

    // Add activities if provided
    if (newItem.activities && newItem.activities.length > 0) {
      apiData.activities = newItem.activities.map(activity => {
        const activityData: any = {
          name: activity.name,
          activityLevel: activity.activityLevel,
          expectedDurationMin: activity.expectedDurationMin
        };
        
        // Include ID for existing activities
        if (activity.id) {
          activityData.id = activity.id;
        } else if (activity.activityTypeId) {
          // For new activities, send the activity type ID (camelCase as per API spec)
          activityData.activityType = activity.activityTypeId;
        }
        
        // Include details if provided
        if (activity.details) {
          activityData.details = activity.details;
        }
        
        return activityData;
      });
    }

    // Add supplements if provided
    if (newItem.supplements && newItem.supplements.length > 0) {
      apiData.supplements = newItem.supplements.map(supplement => {
        const supplementData: any = {
          description: supplement.description
        };

        // Include ID for existing supplements
        if (supplement.id) {
          supplementData.id = supplement.id;
        } else if (supplement.supplementCatalog) {
          // For new supplements, send the catalog ID (camelCase)
          supplementData.supplementCatalogId = supplement.supplementCatalog.id;
        }

        return supplementData;
      });
    }

    // Add medications if provided
    if (newItem.medications && newItem.medications.length > 0) {
      apiData.medications = newItem.medications.map(medication => {
        const medicationData: any = {
          description: medication.description
        };

        if (medication.id) {
          medicationData.id = medication.id;
        } else if (medication.medicationCatalog) {
          medicationData.medicationCatalogId = medication.medicationCatalog.id;
        }

        return medicationData;
      });
    }

    // Add meals (IDs) and meal target for MealItem
    if (newItem.type === 'MealItem') {
      apiData.meals = (newItem.meals || []).map(meal => meal.id);
      if (newItem.mealTarget) {
        apiData.mealTarget = buildMealTargetPayload(newItem.mealTarget);
      }
    }

    try {
      const response = await apiService.post(url, apiData);

      // Convert the response to DiaryItem format
      const createdData = response.data || response;
      return convertApiToDiaryItem(createdData);
    } catch (error) {
      console.error(`Error creating diary item for patient ${patientId}:`, error);
      throw error;
    }
  },

  getDiaryItemsByUserId: async (
    userId: string | number,
    page = 1,
    perpage = 10,
    range?: { start_date?: string; end_date?: string },
  ): Promise<DiaryItemsResponse> => {
    const params = new URLSearchParams({ page: String(page), perpage: String(perpage) });
    if (range?.start_date) params.append('start_date', range.start_date);
    if (range?.end_date) params.append('end_date', range.end_date);
    const url = `${API_BASE_URL}/clinic/patient/${userId}/diary-items?${params.toString()}`;
    try {
      const response = await apiService.get(url);
      return response;
    } catch (error) {
      console.error(`Error fetching diary items for user ${userId}:`, error);
      throw error;
    }
  },

  getAllDiaryItemsByUserId: async (
    userId: string | number,
    range?: { start_date?: string; end_date?: string },
  ): Promise<DiaryItem[]> => {
    try {
      // First, get the first page to know total pages
      const firstPage = await diaryService.getDiaryItemsByUserId(userId, 1, 100, range);

      // If there's only one page, return the results
      if (firstPage.totalPages <= 1) {
        return firstPage.results.map(convertApiToDiaryItem);
      }

      // If there are multiple pages, fetch all pages
      const allPages = await Promise.all(
        Array.from({ length: firstPage.totalPages }, (_, i) =>
          diaryService.getDiaryItemsByUserId(userId, i + 1, 100, range)
        )
      );

      // Combine all results and convert to DiaryItem format
      return allPages.flatMap(page => page.results).map(convertApiToDiaryItem);
    } catch (error) {
      console.error(`Error fetching all diary items for user ${userId}:`, error);
      throw error;
    }
  },

  updateDiaryItem: async (patientId: string | number, diaryItemId: string | number, fullItem: DiaryItem, originalItem?: DiaryItem): Promise<DiaryItem> => {
    const url = `${API_BASE_URL}/clinic/patient/${patientId}/diary-items/${diaryItemId}/`;

    const apiData: any = {};

    const normalize = (v: any) => (v === undefined || v === null ? '' : v === false ? false : v);
    const addIfChanged = (apiKey: string, value: any, originalValue: any) => {
      if (value === undefined) return;
      if (originalItem && normalize(value) === normalize(originalValue)) return;
      apiData[apiKey] = value;
    };

    addIfChanged('name', fullItem.name, originalItem?.name);
    addIfChanged('hidden', fullItem.hidden, originalItem?.hidden);
    addIfChanged('subtype', fullItem.subtype, originalItem?.subtype);
    addIfChanged('day_of_week', fullItem.dayOfWeek, originalItem?.dayOfWeek);
    addIfChanged('time_of_day', fullItem.timeOfDay, originalItem?.timeOfDay);
    addIfChanged('effectiveDate', fullItem.effectiveDate, originalItem?.effectiveDate);
    addIfChanged('cancellationDate', fullItem.cancellationDate, originalItem?.cancellationDate);

    // Meal items: meals (full-set replace) + meal target (upsert)
    if (fullItem.type === 'MealItem') {
      if (fullItem.meals !== undefined) {
        const newIds = fullItem.meals.map(m => m.id);
        const originalIds = (originalItem?.meals || []).map(m => m.id);
        const mealsChanged = !originalItem ||
          newIds.length !== originalIds.length ||
          newIds.some((mealId, i) => mealId !== originalIds[i]);
        if (mealsChanged) apiData.meals = newIds;
      }
      if (fullItem.mealTarget) {
        apiData.mealTarget = buildMealTargetPayload(fullItem.mealTarget);
      }
    }

    // Transform activities if provided (existing activities are read-only — only diff is add/remove)
    if (fullItem.activities !== undefined) {
      const originalActivities = originalItem?.activities || [];
      const activitiesPayload = fullItem.activities.map(activity => {
        if (activity.id) {
          return { id: activity.id };
        }
        const activityData: any = {
          name: activity.name,
          activityLevel: activity.activityLevel,
          expectedDurationMin: activity.expectedDurationMin
        };
        if (activity.activityTypeId) activityData.activityType = activity.activityTypeId;
        if (activity.details) activityData.details = activity.details;
        return activityData;
      });

      const activitiesChanged = !originalItem ||
        originalActivities.length !== fullItem.activities.length ||
        fullItem.activities.some((a, i) => a.id !== originalActivities[i]?.id);
      if (activitiesChanged) apiData.activities = activitiesPayload;
    }

    // Transform supplements if provided — only include changed fields for existing items
    if (fullItem.supplements !== undefined) {
      const originalSupplements = originalItem?.supplements || [];
      const supplementsPayload = fullItem.supplements.map(supplement => {
        if (supplement.id) {
          const original = originalSupplements.find(s => s.id === supplement.id);
          const supplementData: any = { id: supplement.id };
          if (!original || supplement.description !== original.description) {
            supplementData.description = supplement.description;
          }
          if (supplement.supplementCatalog && supplement.supplementCatalog.id !== original?.supplementCatalog?.id) {
            supplementData.supplementCatalogId = supplement.supplementCatalog.id;
          }
          return supplementData;
        }
        const supplementData: any = { description: supplement.description };
        if (supplement.supplementCatalog) {
          supplementData.supplementCatalogId = supplement.supplementCatalog.id;
        }
        return supplementData;
      });

      const supplementsChanged = !originalItem ||
        originalSupplements.length !== fullItem.supplements.length ||
        supplementsPayload.some(s => Object.keys(s).length > 1 || !s.id);
      if (supplementsChanged) apiData.supplements = supplementsPayload;
    }

    // Transform medications if provided — only include changed fields for existing items
    if (fullItem.medications !== undefined) {
      const originalMedications = originalItem?.medications || [];
      const medicationsPayload = fullItem.medications.map(medication => {
        if (medication.id) {
          const original = originalMedications.find(m => m.id === medication.id);
          const medicationData: any = { id: medication.id };
          if (!original || medication.description !== original.description) {
            medicationData.description = medication.description;
          }
          if (medication.medicationCatalog && medication.medicationCatalog.id !== (original as any)?.medicationCatalog?.id) {
            medicationData.medicationCatalogId = medication.medicationCatalog.id;
          }
          return medicationData;
        }
        const medicationData: any = { description: medication.description };
        if (medication.medicationCatalog) {
          medicationData.medicationCatalogId = medication.medicationCatalog.id;
        }
        return medicationData;
      });

      const medicationsChanged = !originalItem ||
        originalMedications.length !== fullItem.medications.length ||
        medicationsPayload.some(m => Object.keys(m).length > 1 || !m.id);
      if (medicationsChanged) apiData.medications = medicationsPayload;
    }

    try {
      const response = await apiService.patch(url, apiData);

      // Handle the nested response structure { data: {...}, message: "..." }
      const updatedData = response.data || response;

      // Create the updated item by merging the original item with the API response
      const updatedItem: DiaryItem = {
        ...fullItem, // Keep all original fields
        // Override with updated fields from API response
        name: updatedData.name,
        dayOfWeek: updatedData.dayOfWeek || updatedData.day_of_week,
        timeOfDay: updatedData.timeOfDay || updatedData.time_of_day,
        hidden: updatedData.hidden,
        subtype: updatedData.subtype,
        activities: updatedData.activities || fullItem.activities,
        supplements: updatedData.supplements || fullItem.supplements,
        medications: updatedData.medications || fullItem.medications,
        meals: updatedData.meals || fullItem.meals,
        mealTarget: updatedData.mealTarget || updatedData.meal_target || fullItem.mealTarget
      };
      
      return updatedItem;
    } catch (error) {
      console.error(`Error updating diary item ${diaryItemId} for patient ${patientId}:`, error);
      throw error;
    }
  }
};